import { Divider, Grid } from "@mui/material";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { formatDataCompare, formatDate, formatDecimal, getLocalStorage, isMobileDevice, setLocalStorage } from "../../../commons/utils";
import { DataTable } from "../../../components/DataTable";
import { Columns, GridCard } from "..";
import { LabelDataTable } from "../../../components/LabelDataTable";
import { ExCard } from "../../../charts/Card";
import { Input } from "../../../components/Input";
import styled from "styled-components";
import moment from "moment";
import BarChart from "../../../charts/Bar";
import { Banknote, CalendarX, HandCoins } from "lucide-react";
import { ExPaper } from "../../../components/Paper";
import { ExSelectFilter, ExSelectFilterHandler } from "../../../components/ExSelectFilter";
import { model_cliente } from "../../../models";
import CircularLoading from "../../../components/CircularLoading";

const RowFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    font-size: 12px;
`
const ColFooter = styled.div`
    width: 150px;
    text-align: right;
`

const CR_SITUACAO_LABEL: { [key: number]: string } = {
    0: 'Aberto',
    1: 'Pago',
    2: 'Cancelado'
};

const CR_SITUACAO_COLOR: { [key: number]: string } = {
    0: '#55127c',
    1: '#086448',
    2: '#b44e13'
};

const columns = [
    { field: 'clinome', header: 'Cliente', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
    { field: 'emissao', header: 'Emissão', body: (e: any) => formatDate(e.emissao) },
    { field: 'crvencimento', header: 'Vencimento', body: (e: any) => formatDate(e.crvencimento) },
    { field: 'movnumero', header: 'Nº Movimento' },
    { field: 'crultimopagamento', header: 'Último Pagamento', body: (e: any) => formatDate(e.crultimopagamento, true) },
    { field: 'formapagamento', header: 'Forma de Pagamento', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
    { field: 'crvalor', header: 'Valor Documento', body: (e: any) => formatDecimal(e.crvalor), dataType: 'numeric' },
    { field: 'valorrecebido', header: 'Valor Recebido', body: (e: any) => formatDecimal(e.valorrecebido), dataType: 'numeric' },
    { field: 'crsaldo', header: 'Valor em Aberto', body: (e: any) => formatDecimal(e.crsaldo), dataType: 'numeric' },
    {
        field: 'crsituacao', header: 'Situação',
        body: (e: any) => {
            const situacaoLabel = (e.crvencimento < formatDataCompare(new Date())) && e.crsituacao === 0 ? 'Vencido' : CR_SITUACAO_LABEL[e.crsituacao];
            const situacaoColor = (e.crvencimento < formatDataCompare(new Date())) && e.crsituacao === 0 ? '#b13939' : CR_SITUACAO_COLOR[e.crsituacao];
            return LabelDataTable(situacaoLabel, situacaoColor);
        },
        style: { width: '125px' }
    }
];

export const DashFinanceiroGeral = forwardRef((props: any, ref) => {
    const refcrveninicial = useRef<any>(null);
    const refcrvenfinal = useRef<any>(null);

    const refCrByVencimento = useRef<any>(null);
    const refTotalAberto = useRef<any>(null);
    const refTotalRecebido = useRef<any>(null);
    const refVencidos = useRef<any>(null);
    const refCliente = useRef<ExSelectFilterHandler>(null)
    const refFilter = useRef<any>({})

    const [sortField, setSortField] = useState('clinome');
    const [sortOrder, setSortOrder] = useState<any>(1);
    const [dataGrid, setDataGrid] = useState<any>([]);
    const [contasReceber, setContasReceber] = useState<any[]>();

    useImperativeHandle(ref, () => ({
        setData
    }));

    useEffect(() => {
        if (!contasReceber) return;

        const ivencimento = getLocalStorage('dash_fin_venc_inicial') ? getLocalStorage('dash_fin_venc_inicial') : moment().format('YYYY-MM-01');

        prePopulate(contasReceber, ivencimento)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contasReceber]);

    function setData(_dataCr: any) {
        setContasReceber(_dataCr);
    }

    function sumTotais(_data: any) {
        const totalCR = _data.reduce((acc: number, item: any) => acc + (item.crvalor || 0), 0);
        const totalAberto = _data.reduce((acc: number, item: any) => acc + (item.crsaldo || 0), 0);

        const totalVencido = _data.reduce((acc: number, item: any) => {
            const isVencido = ((item.crsituacao === 0) && (new Date() > new Date(item.crvencimento)));
            return acc + (isVencido ? item.crsaldo : 0);
        }, 0);


        return { totalAberto: totalAberto, totalRecebido: (totalCR - totalAberto), totalVencido: totalVencido };
    }

    function prePopulate(datagrid: any, ivencimento?: any, fvencimento?: any) {
        if (ivencimento)
            datagrid = datagrid.filter((item: any) => formatDataCompare(item.crvencimento) >= ivencimento);

        if (fvencimento)
            datagrid = datagrid.filter((item: any) => fvencimento > formatDataCompare(item.crvencimento));

        datagrid = refCliente.current?.applyFilter(datagrid) || datagrid

        const filterArr = Object.entries(refFilter.current)
        const dateGridFiltered = datagrid.filter((e: any) => filterArr.every(([key, val]: any) => val(e)))

        setLocalStorage('dash_fin_venc_inicial', ivencimento ?? null);

        populateChart(refCrByVencimento, datagrid.map((e: any) => ({ ...e, total: e.crvalor })), {
            groupByKey: (item: any) => moment(item.crvencimento).format('DD/MM/YYYY'),
            chartTitle: 'Contas a Receber (Por vencimento)',
        });

        populateGrid(dateGridFiltered);
    }

    function populateGrid(_data: any) {
        setDataGrid(_data);
        const _totais: any = sumTotais(_data);

        refTotalAberto.current?.setValue(formatDecimal(_totais.totalAberto));
        refTotalRecebido.current?.setValue(formatDecimal(_totais.totalRecebido));
        refVencidos.current?.setValue(formatDecimal(_totais.totalVencido));
    }

    const getNextMultipleOf1000 = (value: number) => Math.ceil(value / 1000) * 1000;

    const getValue = (groupByKey: string | ((item: any) => string), item: any) => typeof groupByKey === 'function' ? groupByKey(item) : item[groupByKey] || 'Não Informado'

    const groupData = (data: any[], groupByKey: string | ((item: any) => string)) =>
        data.reduce((acc: Record<string, number>, item: any) => {
            const key = getValue(groupByKey, item);
            const value = parseFloat((item.total || 0).toFixed(2));
            acc[key] = (acc[key] || 0) + value;
            return acc;
        }, {});

    const populateChart = (refChart: any, data: any, options: { groupByKey: any; chartTitle: string; limit?: number; type?: 'Bar' | 'Pie'; labelFontSize?: string }) => {
        const { groupByKey, chartTitle, limit = Infinity, type = 'Bar', labelFontSize = '14px' } = options;

        var fdataPointSelection

        if (groupByKey) {
            fdataPointSelection = (e: any, ct: any, { w }: any) => {
                const acceptValues = w.globals.selectedDataPoints[0]?.map((i: number) => w.globals.labels[i]).filter((e: any) => e)

                if (!acceptValues || acceptValues.length === 0)
                    delete refFilter.current[groupByKey]
                else
                    refFilter.current[groupByKey] = (e: any) => acceptValues.includes(getValue(groupByKey, e))

                prePopulate(contasReceber, refcrveninicial.current.getValue(), refcrvenfinal.current.getValue())
            }
        }

        const filterArr = Object.entries(refFilter.current)
        data = [...data].filter((e: any) => filterArr.every(([key, val]: any) => key === groupByKey || val(e)))

        const groupedData = groupData(data, groupByKey);

        let entries: [string, number][];

        if (typeof groupByKey === 'function')
            entries = Object.entries(groupedData).sort(([keyA], [keyB]) => {
                const dateA = moment(keyA, 'DD/MM/YYYY');
                const dateB = moment(keyB, 'DD/MM/YYYY');
                return dateA.diff(dateB);
            }).slice(0, limit);
        else
            entries = Object.entries(groupedData).sort(([, a], [, b]) => b - a).slice(0, limit);

        const labels = entries.map(([key]) => key);
        const values = entries.map(([, value]) => value);

        if (type === 'Bar') {
            refChart.current?.setOptions(labels, getNextMultipleOf1000(Math.max(...values)), chartTitle, undefined, undefined, undefined, labelFontSize, fdataPointSelection);
            refChart.current?.setSeries([{ name: chartTitle, data: values }]);
        } else if (type === 'Pie') {
            refChart.current?.setLabels(labels);
            refChart.current?.setOptions(fdataPointSelection);
            refChart.current?.setSeries(values);
        }
    };

    const groupFooter = (register: any, teste: any) => {
        const filteredData = dataGrid.filter((item: any) => item[sortField] === register[sortField]);
        const total = sumTotais(filteredData);

        return (
            <React.Fragment>
                <td colSpan={12} style={{ padding: '10px' }}>
                    <RowFooter>
                        <ColFooter>Total em Aberto:</ColFooter>
                        <ColFooter>{formatDecimal(total.totalAberto)}</ColFooter>
                    </RowFooter>
                    <RowFooter>
                        <ColFooter>Total Recebido:</ColFooter>
                        <ColFooter>{formatDecimal(total.totalRecebido)}</ColFooter>
                    </RowFooter>
                    <RowFooter>
                        <ColFooter>Total Vencido:</ColFooter>
                        <ColFooter>{formatDecimal(total.totalVencido)}</ColFooter>
                    </RowFooter>
                </td>
            </React.Fragment>
        );
    };

    function changeVencInicial(value: any) {
        prePopulate(contasReceber, value, refcrvenfinal.current.getValue());
    }

    function changeVencFinal(value: any) {
        prePopulate(contasReceber, refcrveninicial.current.getValue(), value)
    }

    function changeFilterCliente() {
        prePopulate(contasReceber, refcrveninicial.current.getValue(), refcrvenfinal.current.getValue())
    }

    return (
        !contasReceber ? <CircularLoading /> :
            <div style={{ padding: isMobileDevice() ? '1rem' : '1rem 4rem' }}>
                <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Grid item xs={12}>
                        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <Input ref={refcrveninicial} id="cr_venc_inicial" type="date" label="Vencimento Inicial" onAfterChange={changeVencInicial}
                                    defaultValue={getLocalStorage('dash_fin_venc_inicial') ? getLocalStorage('dash_fin_venc_inicial') : moment().format('YYYY-MM-01')} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <Input ref={refcrvenfinal} id="cr_venc_final" type="date" label="Vencimento Final" onAfterChange={changeVencFinal} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 8}>
                                <ExSelectFilter
                                    ref={refCliente}
                                    model={model_cliente}
                                    onAfterChange={changeFilterCliente}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} >
                        <Divider sx={{ backgroundColor: '#fff' }} />
                    </Grid>
                </Grid>
                <Grid container spacing={4} sx={{ padding: '15px 0px' }}>
                    <Grid item xs={12}>
                        <BarChart ref={refCrByVencimento} />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <GridCard md={4}>
                                <ExCard ref={refTotalAberto} title='Total em Aberto' icon={<Banknote />} />
                            </GridCard>
                            <GridCard md={4}>
                                <ExCard ref={refTotalRecebido} title='Total Recebido' icon={<HandCoins />} />
                            </GridCard>
                            <GridCard md={4}>
                                <ExCard ref={refVencidos} title='Total Vencidos' icon={<CalendarX />} />
                            </GridCard>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <ExPaper>
                            <DataTable
                                key={sortField}
                                value={dataGrid}
                                originvalues={dataGrid}
                                disabledrefresh={true}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                rowGroupMode={'subheader'}
                                groupRowsBy={sortField}
                                rowGroupFooterTemplate={groupFooter}
                                onSort={(e) => {
                                    setSortOrder(e.sortOrder);
                                    setSortField(e.sortField);
                                }}
                            >
                                {Columns(columns)}
                            </DataTable>
                        </ExPaper>
                    </Grid>
                </Grid >
            </div>
    )
})